import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Building2, Smartphone, Check, ShieldCheck, CheckCircle2,
  Loader2, ArrowLeft, RefreshCw, AlertCircle, Home, Copy, Send, Globe2,
  Clock, ChevronRight,
} from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { FCSBadge } from "@/components/fcs";
import { api } from "@/lib/api";
import { COUNTRIES } from "@/lib/constants";
import { formatAmount } from "@/lib/format";
import { motion, AnimatePresence, ease as mmEase } from "@/lib/motion";
import { useKeepIdleLogoutAlive } from "@/hooks/useIdleLogout";

const ez = mmEase.expo;
const reducedMotion =
  typeof window !== "undefined" &&
  (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false);

const slide = {
  initial:    { opacity: 0, x: 28,  filter: "blur(4px)" },
  animate:    { opacity: 1, x: 0,   filter: "blur(0px)" },
  exit:       { opacity: 0, x: -20, filter: "blur(2px)" },
  transition: { duration: reducedMotion ? 0 : 0.34, ease: ez },
};

// Publishable key only — same pattern as FundWalletModal.jsx. Loaded
// lazily so a build without a real key doesn't crash; the Pay step shows
// a clear "not configured" state instead.
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

// Exact approved copy — do not paraphrase.
const AFRICA_SOURCE_COPY =
  "We're expanding MoneyMeet's payment options across Africa. Receiving money in Africa " +
  "is now our priority. Sending money from African countries will be available soon.";

const METHOD_META = {
  bank:         { label: "Bank transfer", Icon: Building2, accent: "#10B981" },
  mobile_money: { label: "Mobile money",  Icon: Smartphone, accent: "#7C3AED" },
};

const STEPS = [
  { key: "corridor",  label: "Corridor"  },
  { key: "amount",    label: "Amount"    },
  { key: "recipient", label: "Recipient" },
  { key: "review",    label: "Review"    },
  { key: "pay",       label: "Pay"       },
];

// Customer-facing progress tracker — maps the backend's own status codes
// (settlement/customer_status.py) onto 4 truthful nodes. A node is only
// ever shown as reached once the backend has actually said so via the
// status poll below; nothing here is guessed or advanced optimistically.
const PROGRESS_NODES = [
  { key: "payment",    label: "Payment confirmed"   },
  { key: "compliance",  label: "Security checks"     },
  { key: "sending",     label: "Sending to recipient" },
  { key: "delivered",   label: "Delivered"            },
];
function progressIndexForStatus(code) {
  switch (code) {
    case "funding_pending":                return -1;
    case "funding_confirmed":              return 0;
    case "compliance_review":              return 1;
    case "payout_pending":                 return 1;
    case "payout_processing":              return 2;
    case "payout_completed":               return 3;
    default:                               return -1;
  }
}
const BADGE_VARIANT_FOR_STATUS = {
  funding_pending:   "pending",
  funding_confirmed: "processing",
  compliance_review: "warning",
  payout_pending:    "pending",
  payout_processing: "processing",
  payout_completed:  "success",
  payout_failed:     "failure",
  funding_failed:    "failure",
};

// The step-6 "done" screen used one generic "We hit a snag" for every
// non-success status, with no explanation of what actually happened or
// what to do next — funding_failed (the card payment itself didn't go
// through; nothing was taken) and payout_failed (payment succeeded,
// delivery to the recipient did not; the money is being handled, not
// lost) are very different situations for a customer to be told about.
// No internal detail (provider names, error codes) belongs here — this
// is customer-facing copy, not a debug log.
const DONE_COPY_FOR_STATUS = {
  payout_completed: {
    heading: "Delivered.",
    body: null,
  },
  funding_failed: {
    heading: "Payment didn't go through",
    body: "Nothing was taken from your card. You can try again with the same or a different card.",
  },
  payout_failed: {
    heading: "We couldn't complete this transfer",
    body: "Your payment was received — nothing has been lost. Delivery to your recipient didn't go through, and our team has been notified. Contact support with the reference below if you don't hear from us shortly.",
  },
};
const DEFAULT_DONE_COPY = {
  heading: "We hit a snag",
  body: "Contact support with the reference below and we'll look into it.",
};

function countryMeta(code) {
  return COUNTRIES.find((c) => c.code === code);
}

export default function SendMoney() {
  return <RemittanceSendMoney />;
}

function RemittanceSendMoney() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0 corridor, 1 amount, 2 recipient, 3 review, 4 pay, 5 processing, 6 done

  const [corridors, setCorridors]           = useState(null);
  const [corridorsLoading, setCorridorsLoading] = useState(true);
  const [corridorsError, setCorridorsError] = useState(false);

  const [sourceCountry, setSourceCountry]   = useState("");
  const [destCountry, setDestCountry]       = useState("");
  const [method, setMethod]                 = useState("");
  const [amount, setAmount]                 = useState(200);

  const [quote, setQuote]           = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [quoteSecsLeft, setQuoteSecsLeft] = useState(0);
  // Absolute expiry (epoch ms), not just a starting second count — real
  // bug fix: the countdown used to only start ticking once step reached
  // Review, so time spent on the recipient step wasn't reflected and the
  // displayed number could overstate what the backend's own Redis TTL
  // actually had left. Anchoring to a real timestamp keeps it accurate
  // regardless of which step the user is on.
  const quoteExpiresAtRef = useRef(0);

  const [recipient, setRecipient] = useState({
    name: "", bank_name: "", bank_code: "", account_number: "", mobile_number: "",
  });

  // Bug B fix — real bank codes, not free-typed names. Loaded fresh
  // whenever the destination country changes; a customer must select
  // from this list, never type a bank name, so bank_code is always a
  // real provider-issued code.
  const [banks, setBanks]               = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [banksError, setBanksError]     = useState(false);

  const [intent, setIntent]         = useState(null); // {id, settlement_id, client_secret}
  const [payError, setPayError]     = useState(null);
  const [paySubmitting, setPaySubmitting] = useState(false);

  const [statusData, setStatusData]   = useState(null);
  const [statusTimedOut, setStatusTimedOut] = useState(false);

  // ── Load the corridor registry — the ONLY source of truth for what's
  // selectable. Nothing about destination/method availability is
  // hardcoded here; it all comes straight from GET /api/corridors, which
  // itself derives status from real, live provider capability checks.
  useEffect(() => {
    api.get("/corridors")
      .then(({ data }) => setCorridors(data))
      .catch(() => setCorridorsError(true))
      .finally(() => setCorridorsLoading(false));
  }, []);

  // Bug B fix — fetch the destination's real bank list from MoneyMeet's
  // own backend (never Flutterwave directly — the browser must never see
  // a provider secret) whenever the bank-transfer method and a
  // destination are both chosen. Clears any previously-selected bank on
  // a destination change, since a bank code from one country is
  // meaningless for another.
  useEffect(() => {
    if (method !== "bank" || !destCountry) { setBanks([]); return; }
    setBanksError(false);
    setBanksLoading(true);
    setRecipient((r) => ({ ...r, bank_name: "", bank_code: "" }));
    api.get(`/remittance/banks/${destCountry}`)
      .then(({ data }) => setBanks(data.banks || []))
      .catch(() => { setBanks([]); setBanksError(true); })
      .finally(() => setBanksLoading(false));
  }, [method, destCountry]);

  const africanCountries = useMemo(
    () => new Set((corridors?.destinations || []).map((d) => d.destination_country)),
    [corridors]
  );

  // Data-driven Africa-outbound guard — matches the destination list
  // itself (never a second hardcoded list), so it self-corrects the
  // moment the corridor registry changes.
  const sourceIsAfrican = sourceCountry && africanCountries.has(sourceCountry);

  const destinationsByCountry = useMemo(() => {
    const map = new Map();
    for (const row of corridors?.destinations || []) {
      if (!map.has(row.destination_country)) {
        map.set(row.destination_country, {
          country: row.destination_country,
          name: row.destination_name,
          currency: row.destination_currency,
          methods: [],
        });
      }
      map.get(row.destination_country).methods.push(row);
    }
    return Array.from(map.values());
  }, [corridors]);

  const bestStatusForCountry = (methods) => {
    if (methods.some((m) => m.status === "LIVE")) return "LIVE";
    if (methods.some((m) => m.status === "SANDBOX_READY")) return "SANDBOX_READY";
    return "COMING_SOON";
  };

  const selectedDestRow = destinationsByCountry.find((d) => d.country === destCountry);
  const availableMethods = (selectedDestRow?.methods || []).filter(
    (m) => m.status === "LIVE" || m.status === "SANDBOX_READY"
  );

  // ── Quote fetch — debounced, re-runs whenever corridor/amount changes.
  useEffect(() => {
    if (step !== 1) return;
    if (!sourceCountry || !destCountry || !method || !amount || amount <= 0) { setQuote(null); return; }
    setQuoteError(null);
    setQuoteLoading(true);
    const t = setTimeout(() => {
      api.post("/remittance/quote", {
        source_country: sourceCountry, destination_country: destCountry,
        send_amount: Number(amount), payout_method: method,
      })
        .then(({ data }) => {
          setQuote(data);
          setQuoteSecsLeft(data.quote_expires_in_seconds);
          quoteExpiresAtRef.current = Date.now() + data.quote_expires_in_seconds * 1000;
        })
        .catch((e) => {
          setQuote(null);
          setQuoteError(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not get a quote for this transfer.");
        })
        .finally(() => setQuoteLoading(false));
    }, 350);
    return () => clearTimeout(t);
  }, [step, sourceCountry, destCountry, method, amount]);

  // Quote countdown — purely informational (the backend re-validates the
  // token's real expiry on submit); nudges the user to refresh before
  // paying with a stale rate.
  useEffect(() => {
    if (!quote) return;
    const tick = () => {
      const secsLeft = Math.max(0, Math.round((quoteExpiresAtRef.current - Date.now()) / 1000));
      setQuoteSecsLeft(secsLeft);
    };
    tick(); // reflect real elapsed time immediately, not just from the next tick
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [quote]);

  const refreshQuote = () => {
    if (!sourceCountry || !destCountry || !method || !amount) return;
    setQuoteLoading(true);
    api.post("/remittance/quote", {
      source_country: sourceCountry, destination_country: destCountry,
      send_amount: Number(amount), payout_method: method,
    })
      .then(({ data }) => {
        setQuote(data);
        setQuoteSecsLeft(data.quote_expires_in_seconds);
        quoteExpiresAtRef.current = Date.now() + data.quote_expires_in_seconds * 1000;
        setQuoteError(null);
      })
      .catch((e) => setQuoteError(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not refresh the quote."))
      .finally(() => setQuoteLoading(false));
  };

  // ── Status polling — starts once the payment intent is created and
  // Stripe confirms client-side; the backend's own webhook-driven status
  // is the only thing that ever moves a step forward here.
  useEffect(() => {
    if (step !== 5 || !intent?.settlement_id) return;
    let attempts = 0;
    const iv = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/remittance/${intent.settlement_id}/status`);
        setStatusData(data);
        if (["payout_completed", "payout_failed", "funding_failed"].includes(data.status)) {
          clearInterval(iv);
          setStep(6);
        }
      } catch { /* transient — keep polling */ }
      if (attempts >= 90) { // ~3 minutes
        clearInterval(iv);
        setStatusTimedOut(true);
      }
    }, 2000);
    return () => clearInterval(iv);
  }, [step, intent]);

  const deliveryLabel = quote?.estimated_delivery || "—";
  const progressIdx = statusData ? progressIndexForStatus(statusData.status) : -1;

  const canProceedCorridor  = sourceCountry && destCountry && method && !sourceIsAfrican && availableMethods.length > 0;
  const canProceedAmount    = !!quote && !quoteError;
  const canProceedRecipient = recipient.name.trim().length > 0 && (
    // bank_code required, not just bank_name — a selection from the real
    // list, never a free-typed name standing in for it (Bug B fix).
    method === "bank"         ? recipient.bank_code && recipient.account_number :
    method === "mobile_money" ? recipient.mobile_number : false
  );

  const startPayment = async () => {
    if (!quote) return;
    setPayError(null);
    setPaySubmitting(true);
    const idempotencyKey = `rmt_${crypto.randomUUID()}`;
    try {
      const { data } = await api.post(
        "/remittance/initiate",
        {
          quote_token: quote.quote_token,
          recipient: {
            name: recipient.name, country: destCountry, method,
            bank_name: method === "bank" ? recipient.bank_name : undefined,
            bank_code: method === "bank" ? recipient.bank_code : undefined,
            account_number: method === "bank" ? recipient.account_number : undefined,
            mobile_number: method === "mobile_money" ? recipient.mobile_number : undefined,
          },
        },
        { headers: { "Idempotency-Key": idempotencyKey } }
      );
      setIntent(data);
      setPaySubmitting(false);
      setStep(4);
    } catch (e) {
      setPayError(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not start this transfer. Please try again.");
      setPaySubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: -14, filter: "blur(4px)" }}
        animate={{ opacity: 1, y: 0,   filter: "blur(0px)" }}
        transition={{ duration: reducedMotion ? 0 : 0.45, ease: ez }}
        className="mb-7"
      >
        <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-1">Transfer</p>
        <h1 className="font-display text-[1.65rem] font-black text-navy tracking-tight">Send money</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Transparent pricing. No surprises.</p>
      </motion.div>

      {step < 5 && (
        <motion.div className="mb-7" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reducedMotion ? 0 : 0.35, ease: ez, delay: 0.05 }}>
          <div className="flex items-center gap-0">
            {STEPS.map((s, i) => (
              <div key={s.key} className="flex items-center flex-1 last:flex-none">
                <button
                  data-testid={`wizard-step-${i}`}
                  onClick={() => i < step && setStep(i)}
                  className={`relative flex flex-col items-center gap-1.5 ${i <= step ? "cursor-pointer" : "cursor-default"}`}
                >
                  <div className={`w-8 h-8 rounded-full grid place-items-center text-xs font-bold transition-all duration-300 ${
                    i < step ? "bg-emerald text-white shadow-[0_0_16px_rgba(16,185,129,0.45)]"
                    : i === step ? "bg-navy text-white shadow-[0_0_0_3px_rgba(11,29,58,0.10)]"
                    : "bg-secondary text-muted-foreground border border-border/80"
                  }`}>
                    {i < step ? <Check size={13} strokeWidth={2.5} /> : <span>{i + 1}</span>}
                  </div>
                  <span className={`hidden sm:block text-[10px] font-bold transition-colors whitespace-nowrap ${
                    i === step ? "text-navy" : i < step ? "text-emerald" : "text-muted-foreground/60"
                  }`}>{s.label}</span>
                </button>
                {i < STEPS.length - 1 && (
                  <div className="flex-1 mx-2 sm:mx-3 h-0.5 rounded-full overflow-hidden bg-border/60">
                    <motion.div className="h-full bg-emerald rounded-full" initial={{ width: "0%" }}
                      animate={{ width: i < step ? "100%" : "0%" }}
                      transition={{ duration: reducedMotion ? 0 : 0.45, ease: ez }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}

      <AnimatePresence mode="wait" initial={false}>

        {/* STEP 0 — Corridor */}
        {step === 0 && (
          <motion.div key="s0" {...slide} data-testid="step-corridor">
            <div className="card-elevated p-6">
              <h3 className="font-display text-xl font-black text-navy mb-0.5 tracking-tight">Where from, where to?</h3>
              <p className="text-sm text-muted-foreground mb-5">
                Choose where you're sending from and who's receiving it.
              </p>

              {corridorsLoading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
                  <Loader2 size={16} className="animate-spin" /> Loading available destinations…
                </div>
              )}

              {!corridorsLoading && corridorsError && (
                <p className="text-sm text-danger py-6 text-center">
                  Could not load transfer destinations. Please refresh the page.
                </p>
              )}

              {!corridorsLoading && !corridorsError && (
                <>
                  <div className="mb-5">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3">Sending from</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {(corridors.funding_markets || []).map((m) => (
                        <button
                          key={m.country}
                          data-testid={`source-country-${m.country}`}
                          aria-pressed={sourceCountry === m.country}
                          onClick={() => setSourceCountry(m.country)}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-left text-sm font-semibold transition-all ${
                            sourceCountry === m.country
                              ? "border-emerald bg-emerald/[0.05] shadow-[0_0_0_2px_rgba(16,185,129,0.14)]"
                              : "border-border hover:border-emerald/30 hover:bg-secondary/40"
                          }`}
                        >
                          <span>{countryMeta(m.country)?.flag || "🌍"}</span>
                          <span className="truncate text-navy">{m.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {sourceIsAfrican ? (
                    <div
                      data-testid="africa-source-coming-soon"
                      className="rounded-2xl border border-emerald/20 p-5 flex items-start gap-3"
                      style={{ background: "rgba(16,185,129,0.04)" }}
                    >
                      <Globe2 size={18} className="text-emerald shrink-0 mt-0.5" strokeWidth={2} />
                      <p className="text-sm text-navy leading-relaxed">{AFRICA_SOURCE_COPY}</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3">Sending to</p>
                      {destinationsByCountry.length === 0 && (
                        <p className="text-sm text-muted-foreground py-4">No destinations configured yet.</p>
                      )}
                      <div className="space-y-2">
                        {destinationsByCountry.map((d) => {
                          const overall = bestStatusForCountry(d.methods);
                          const selectable = overall !== "COMING_SOON";
                          const active = destCountry === d.country;
                          return (
                            <button
                              key={d.country}
                              data-testid={`dest-country-${d.country}`}
                              disabled={!selectable}
                              aria-pressed={active}
                              onClick={() => { setDestCountry(d.country); setMethod(""); }}
                              className={`w-full flex items-center justify-between gap-3 p-4 rounded-2xl border transition-all text-left ${
                                !selectable ? "border-border/60 opacity-55 cursor-not-allowed"
                                : active ? "border-emerald bg-emerald/[0.04] shadow-[0_0_0_3px_rgba(16,185,129,0.10)]"
                                : "border-border hover:border-emerald/40 hover:bg-secondary/50"
                              }`}
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <span className="text-lg">{countryMeta(d.country)?.flag || "🌍"}</span>
                                <div className="min-w-0">
                                  <p className="font-semibold text-navy text-sm">{d.name}</p>
                                  <p className="text-[11px] text-muted-foreground">{d.currency}</p>
                                </div>
                              </div>
                              {/* "processing" is FCSBadge's animate-spin variant, meant
                                  for a genuinely in-flight state — reused here for a
                                  static "Sandbox testing" status label, it spun forever
                                  and looked like a stuck/broken loading indicator. This
                                  corridor's readiness isn't loading; it's a settled fact
                                  until someone flips it. "pending" (a still clock icon)
                                  says the same thing without implying anything is
                                  actively happening. */}
                              {selectable
                                ? <FCSBadge variant={overall === "LIVE" ? "success" : "pending"} label={overall === "LIVE" ? "Available" : "Sandbox testing"} size="sm" />
                                : <FCSBadge variant="pending" label="Coming soon" size="sm" />}
                            </button>
                          );
                        })}
                      </div>

                      <AnimatePresence>
                        {selectedDestRow && availableMethods.length > 0 && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }} transition={{ duration: reducedMotion ? 0 : 0.28, ease: ez }}
                            className="mt-5 overflow-hidden"
                          >
                            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-3">Delivery method</p>
                            <div className="grid sm:grid-cols-2 gap-2">
                              {availableMethods.map((m) => {
                                const meta = METHOD_META[m.method] || { label: m.method, Icon: Building2, accent: "#10B981" };
                                const active = method === m.method;
                                return (
                                  <button
                                    key={m.method}
                                    data-testid={`method-${m.method}`}
                                    aria-pressed={active}
                                    onClick={() => setMethod(m.method)}
                                    className={`flex items-center gap-2.5 p-3.5 rounded-2xl border text-left transition-all ${
                                      active ? "border-transparent" : "border-border hover:border-emerald/25 hover:bg-secondary/40"
                                    }`}
                                    style={active ? { borderColor: meta.accent, background: `${meta.accent}0D`, boxShadow: `0 0 0 2px ${meta.accent}40` } : {}}
                                  >
                                    <meta.Icon size={16} strokeWidth={1.8} style={{ color: active ? meta.accent : "#475569" }} />
                                    <span className="font-semibold text-navy text-sm">{meta.label}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </>
              )}
            </div>

            {!sourceIsAfrican && (
              <div className="mt-5 flex justify-end">
                <motion.button
                  disabled={!canProceedCorridor}
                  data-testid="corridor-next"
                  onClick={() => setStep(1)}
                  className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                  whileHover={canProceedCorridor ? { scale: 1.02 } : {}}
                  whileTap={canProceedCorridor ? { scale: 0.97 } : {}}
                >
                  Amount <ArrowRight size={14} />
                </motion.button>
              </div>
            )}
          </motion.div>
        )}

        {/* STEP 1 — Amount / Quote */}
        {step === 1 && (
          <motion.div key="s1" {...slide} data-testid="step-amount">
            <div className="card-elevated overflow-hidden">
              <div className="p-6 border-b border-border/60">
                <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-2">You send</p>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    data-testid="send-amount-input"
                    value={amount}
                    min={0}
                    onChange={(e) => setAmount(Math.max(0, Number(e.target.value)))}
                    className="text-[2.8rem] font-display font-black text-navy bg-transparent outline-none flex-1 num-tabular w-0 min-w-0 tracking-tight"
                  />
                  <span className="text-lg font-bold text-navy">{quote?.source_currency || countryMeta(sourceCountry)?.currency}</span>
                </div>
              </div>

              <div className="p-6 bg-navy/[0.025] border-t border-border/60">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Recipient gets</p>
                  {quote && (
                    <p className="text-[11px] font-mono font-bold text-emerald">
                      1 {quote.source_currency} = {quote.exchange_rate.toFixed(4)} {quote.destination_currency}
                    </p>
                  )}
                </div>
                <div className="text-[2.8rem] font-display font-black text-emerald num-tabular tracking-tight" data-testid="send-receive-amount">
                  {quoteLoading
                    ? <Loader2 size={28} className="animate-spin text-emerald" />
                    : quote
                    ? quote.recipient_receives.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    : "0.00"}
                  {quote && <span className="text-lg text-emerald/60 ml-2">{quote.destination_currency}</span>}
                </div>
              </div>

              {quoteError && (
                <div role="alert" className="px-6 py-4 text-sm text-danger border-t border-border/60">{quoteError}</div>
              )}

              {quote && !quoteError && (
                <div className="border-t border-border/60">
                  <div className="grid grid-cols-3 gap-px bg-border/50">
                    <QuotePill label="Fee" value={formatAmount(quote.fee, quote.source_currency)} />
                    <QuotePill label="Exchange rate" value={quote.exchange_rate.toFixed(4)} />
                    <QuotePill label="Arrival" value={deliveryLabel} highlight />
                  </div>
                  <div className="px-4 py-3 flex items-center justify-between border-t border-emerald/15" style={{ background: "rgba(16,185,129,0.04)" }}>
                    <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-[0.12em]">Total to pay</span>
                    <span className="font-display font-black text-navy num-tabular text-base" data-testid="send-total-preview">
                      {formatAmount(Number(quote.send_amount) + Number(quote.fee), quote.source_currency)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-5 flex justify-between">
              <button onClick={() => setStep(0)} data-testid="step-back-1"
                className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-navy transition-colors px-2 py-2">
                <ArrowLeft size={14} /> Back
              </button>
              <motion.button
                disabled={!canProceedAmount}
                data-testid="amount-next"
                onClick={() => setStep(2)}
                className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                whileHover={canProceedAmount ? { scale: 1.02 } : {}}
                whileTap={canProceedAmount ? { scale: 0.97 } : {}}
              >
                Recipient <ArrowRight size={14} />
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* STEP 2 — Recipient */}
        {step === 2 && (
          <motion.div key="s2" {...slide} data-testid="step-recipient">
            <div className="card-elevated p-6">
              <h3 className="font-display text-xl font-black text-navy mb-0.5 tracking-tight">Who's receiving?</h3>
              <p className="text-sm text-muted-foreground mb-5">
                Only the details {METHOD_META[method]?.label.toLowerCase() || "this method"} needs.
              </p>
              <div className="space-y-3">
                <input
                  data-testid="recipient-name-input"
                  placeholder="Full name"
                  value={recipient.name}
                  onChange={(e) => setRecipient({ ...recipient, name: e.target.value })}
                  className="input-field w-full"
                />
                {method === "bank" && (
                  <>
                    {/* Bug B fix — a real bank, selected from the provider's
                        own list, never a free-typed name standing in for a
                        bank code Flutterwave actually needs. No silent
                        fallback to free text if the list fails to load —
                        the customer is told plainly and blocked from
                        continuing, rather than the app quietly
                        constructing a transfer request that can't work. */}
                    {banksLoading && (
                      <div className="input-field w-full flex items-center gap-2 text-muted-foreground" role="status" aria-live="polite">
                        <Loader2 size={14} className="animate-spin" /> Loading banks…
                      </div>
                    )}
                    {!banksLoading && banksError && (
                      <div role="alert" className="px-4 py-3 rounded-xl text-sm text-danger" style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.18)" }}>
                        The bank list is temporarily unavailable. Please try again in a moment.
                      </div>
                    )}
                    {!banksLoading && !banksError && banks.length === 0 && (
                      <div role="alert" className="px-4 py-3 rounded-xl text-sm text-muted-foreground" style={{ background: "rgba(0,0,0,0.03)", border: "1px solid var(--border)" }}>
                        No banks are currently available for this destination.
                      </div>
                    )}
                    {!banksLoading && !banksError && banks.length > 0 && (
                      <select
                        data-testid="recipient-bank-select"
                        aria-label="Recipient's bank"
                        value={recipient.bank_code}
                        onChange={(e) => {
                          const bank = banks.find((b) => b.code === e.target.value);
                          setRecipient({ ...recipient, bank_code: bank?.code || "", bank_name: bank?.name || "" });
                        }}
                        className="input-field w-full"
                      >
                        <option value="">Select bank</option>
                        {banks.map((b) => (
                          <option key={b.code} value={b.code}>{b.name}</option>
                        ))}
                      </select>
                    )}
                    <input
                      data-testid="recipient-bank-account"
                      placeholder="Account number"
                      value={recipient.account_number}
                      onChange={(e) => setRecipient({ ...recipient, account_number: e.target.value })}
                      className="input-field w-full"
                    />
                  </>
                )}
                {method === "mobile_money" && (
                  <input
                    data-testid="recipient-mobile-input"
                    placeholder="Mobile money number (e.g., +254712345678)"
                    value={recipient.mobile_number}
                    onChange={(e) => setRecipient({ ...recipient, mobile_number: e.target.value })}
                    className="input-field w-full"
                  />
                )}
              </div>
            </div>
            <div className="mt-5 flex justify-between">
              <button onClick={() => setStep(1)} data-testid="step-back-2"
                className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-navy transition-colors px-2 py-2">
                <ArrowLeft size={14} /> Back
              </button>
              <motion.button
                disabled={!canProceedRecipient}
                data-testid="recipient-next"
                onClick={() => setStep(3)}
                className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                whileHover={canProceedRecipient ? { scale: 1.02 } : {}}
                whileTap={canProceedRecipient ? { scale: 0.97 } : {}}
              >
                Review <ArrowRight size={14} />
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* STEP 3 — Review */}
        {step === 3 && quote && (
          <motion.div key="s3" {...slide} data-testid="step-review">
            <div className="card-elevated overflow-hidden">
              <div className="brand-gradient px-6 pt-6 pb-8 sm:px-7 sm:pt-7 relative overflow-hidden">
                <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(16,185,129,0.18)" }} />
                <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-4 relative">Transfer summary</p>
                <div className="flex items-end justify-between relative">
                  <div>
                    <p className="text-white/45 text-xs mb-1">You send</p>
                    <p className="font-display text-3xl font-black text-white num-tabular tracking-tight">
                      {formatAmount(quote.send_amount, quote.source_currency)}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-2xl bg-white/[0.08] border border-white/10 grid place-items-center">
                    <ArrowRight size={16} className="text-white/60" />
                  </div>
                  <div className="text-right">
                    <p className="text-white/45 text-xs mb-1">They receive</p>
                    <p className="font-display text-3xl font-black text-emerald num-tabular tracking-tight">
                      {formatAmount(quote.recipient_receives, quote.destination_currency)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="px-5 sm:px-7 py-2.5 border-b border-border/50">
                <div className="flex items-center justify-between mb-1.5" data-testid="send-quote-timer">
                  <span className="flex items-center gap-1 font-mono text-[12px] font-bold" style={{ color: quoteSecsLeft > 60 ? "#06B6D4" : quoteSecsLeft > 20 ? "#F59E0B" : "#EF4444" }}>
                    <Clock size={12} strokeWidth={2.5} />
                    Rate locked · {String(Math.floor(Math.max(0, quoteSecsLeft) / 60)).padStart(2, "0")}:{String(Math.max(0, quoteSecsLeft) % 60).padStart(2, "0")}
                  </span>
                  {quoteSecsLeft <= 20 && (
                    <button onClick={refreshQuote} data-testid="quote-refresh" className="text-[10.5px] font-bold text-emerald flex items-center gap-1">
                      <RefreshCw size={10} /> Refresh
                    </button>
                  )}
                </div>
                <div className="h-[3px] rounded-full bg-border/60 overflow-hidden">
                  <div className="h-full rounded-full" style={{
                    width: `${Math.min(100, (quoteSecsLeft / quote.quote_expires_in_seconds) * 100)}%`,
                    background: quoteSecsLeft > 60 ? "#06B6D4" : quoteSecsLeft > 20 ? "#F59E0B" : "#EF4444",
                    transition: reducedMotion ? "none" : "width 1s linear, background 0.4s ease",
                  }} />
                </div>
              </div>

              <div className="p-5 sm:p-7 space-y-3.5">
                <ReviewRow label="Recipient" value={recipient.name} />
                <ReviewRow label="Destination" value={`${countryMeta(destCountry)?.flag || ""} ${countryMeta(destCountry)?.name || destCountry}`} />
                <ReviewRow label="Delivery method" value={METHOD_META[method]?.label || method} />
                <ReviewRow label="Fee" value={formatAmount(quote.fee, quote.source_currency)} />
                <div className="flex items-center justify-between text-sm py-2 px-3 rounded-xl" style={{ background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.18)" }} data-testid="send-total-debit">
                  <span className="font-semibold" style={{ color: "#10B981" }}>Total to pay</span>
                  <span className="font-display font-bold text-base num-tabular text-navy">
                    {formatAmount(Number(quote.send_amount) + Number(quote.fee), quote.source_currency)}
                  </span>
                </div>
                <ReviewRow label="Estimated arrival" value={<FCSBadge variant="pending" label={deliveryLabel} size="sm" />} />

                <div className="flex flex-wrap gap-2 pt-1">
                  {[
                    { key: "rate",   Icon: ShieldCheck,  label: "Rate locked",       color: "#06B6D4" },
                    { key: "secure", Icon: ShieldCheck,  label: "Secure connection", color: "#10B981" },
                  ].map((tci) => (
                    <div key={tci.key} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-[10.5px] font-semibold"
                      style={{ background: `${tci.color}10`, border: `1px solid ${tci.color}28`, color: tci.color }}>
                      <tci.Icon size={10} strokeWidth={2.5} /> {tci.label}
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground leading-relaxed pt-1">
                  This rate is locked until the "Rate locked" timer at the top of this card reaches
                  00:00. Confirm your payment before then — if it expires, request a fresh quote.
                </p>
              </div>
            </div>

            {payError && (
              <div role="alert" className="mt-4 px-4 py-3 rounded-2xl text-sm text-danger" style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.18)" }}>
                {payError}
              </div>
            )}

            <div className="mt-5 flex justify-between">
              <button onClick={() => setStep(2)} data-testid="step-back-3"
                className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-navy transition-colors px-2 py-2">
                <ArrowLeft size={14} /> Back
              </button>
              <motion.button
                data-testid="review-confirm"
                onClick={startPayment}
                disabled={paySubmitting || quoteSecsLeft <= 0}
                className="btn-primary min-w-[160px] justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                whileHover={{ scale: paySubmitting ? 1 : 1.02 }}
                whileTap={{ scale: paySubmitting ? 1 : 0.97 }}
              >
                {paySubmitting ? <><Loader2 size={14} className="animate-spin" /> Starting…</> : <>Continue to payment <ChevronRight size={14} /></>}
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* STEP 4 — Pay (Stripe Elements) */}
        {step === 4 && intent && (
          <motion.div key="s4" {...slide} data-testid="step-pay">
            {stripePromise ? (
              <Elements stripe={stripePromise}>
                <PayStep
                  intent={intent}
                  quote={quote}
                  onBack={() => setStep(3)}
                  onPaid={() => { setStatusData(null); setStatusTimedOut(false); setStep(5); }}
                />
              </Elements>
            ) : (
              <div className="card-elevated p-6 text-center">
                <p className="text-sm text-muted-foreground">Card payments are not configured for this deployment.</p>
              </div>
            )}
          </motion.div>
        )}

        {/* STEP 5 — Processing */}
        {step === 5 && (
          <motion.div key="s5" initial={{ opacity: 0, scale: reducedMotion ? 1 : 0.94, y: reducedMotion ? 0 : 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: reducedMotion ? 0 : 0.45, ease: ez }} data-testid="step-processing">
            <div className="card-elevated overflow-hidden">
              <div className="brand-gradient px-6 py-12 sm:py-14 relative overflow-hidden text-center">
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                <div role="status" aria-live="polite" aria-atomic="true" aria-busy="true">
                  {!statusTimedOut ? (
                    <>
                      <Loader2 size={30} className="text-emerald animate-spin mx-auto mb-4" />
                      <p className="font-display text-xl font-black text-white tracking-tight mb-1.5" data-testid="send-processing-phase">
                        {statusData?.label || "Confirming your payment…"}
                      </p>
                      {statusData?.status === "compliance_review" && (
                        <p className="text-sm text-white/60 max-w-sm mx-auto mt-2">
                          Your transfer is under a brief manual review — this is routine for some transfers
                          and doesn't mean anything is wrong. We'll update you here.
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <Clock size={28} className="text-white/70 mx-auto mb-4" />
                      <p className="font-display text-xl font-black text-white tracking-tight mb-1.5">Still on its way</p>
                      <p className="text-sm text-white/60 max-w-sm mx-auto">
                        This is taking longer than usual. Your payment was received — check your transaction
                        history in a few minutes for the latest status.
                      </p>
                    </>
                  )}
                </div>
              </div>
              {!statusTimedOut && (
                <div className="px-6 py-6 flex justify-center gap-3">
                  {PROGRESS_NODES.map((n, i) => (
                    <div key={n.key} className="flex flex-col items-center gap-1.5 w-20">
                      <div className={`w-7 h-7 rounded-full grid place-items-center text-[10px] font-bold transition-all ${
                        i < progressIdx ? "bg-emerald text-white" : i === progressIdx ? "bg-navy text-white" : "bg-secondary text-muted-foreground"
                      }`}>
                        {i < progressIdx ? <Check size={11} /> : i + 1}
                      </div>
                      <span className={`text-[9.5px] text-center font-semibold leading-tight ${i <= progressIdx ? "text-navy" : "text-muted-foreground/60"}`}>
                        {n.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {statusTimedOut && (
                <div className="px-6 pb-6 text-center">
                  <button onClick={() => navigate("/app/transactions")} className="btn-primary">
                    View transaction history <ArrowRight size={14} />
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* STEP 6 — Done */}
        {step === 6 && statusData && (
          <motion.div key="s6" initial={{ opacity: 0, scale: reducedMotion ? 1 : 0.92, y: reducedMotion ? 0 : 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: reducedMotion ? 0 : 0.55, ease: ez }} data-testid="step-done">
            <div className="card-elevated overflow-hidden">
              <div className="brand-gradient py-12 px-6 relative overflow-hidden text-center">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full blur-3xl pointer-events-none"
                  style={{ background: statusData.status === "payout_completed" ? "rgba(16,185,129,0.28)" : "rgba(239,68,68,0.22)" }} />
                <div className="relative">
                  <div className="w-20 h-20 mx-auto mb-5 rounded-full grid place-items-center" style={{
                    background: statusData.status === "payout_completed" ? "#10B981" : "#EF4444",
                    boxShadow: statusData.status === "payout_completed"
                      ? "0 0 0 8px rgba(16,185,129,0.18), 0 0 40px rgba(16,185,129,0.45)"
                      : "0 0 0 8px rgba(239,68,68,0.18), 0 0 40px rgba(239,68,68,0.35)",
                  }}>
                    {statusData.status === "payout_completed"
                      ? <CheckCircle2 size={38} className="text-white" strokeWidth={2} />
                      : <AlertCircle size={38} className="text-white" strokeWidth={2} />}
                  </div>
                  <h3 className="font-display text-3xl font-black text-white tracking-tight">
                    {(DONE_COPY_FOR_STATUS[statusData.status] || DEFAULT_DONE_COPY).heading}
                  </h3>
                  {(DONE_COPY_FOR_STATUS[statusData.status] || DEFAULT_DONE_COPY).body && (
                    <p className="text-white/70 text-sm max-w-sm mx-auto mt-2 leading-relaxed">
                      {(DONE_COPY_FOR_STATUS[statusData.status] || DEFAULT_DONE_COPY).body}
                    </p>
                  )}
                  {statusData.reference && (
                    <div className="flex items-center justify-center gap-1.5 mt-3">
                      <span className="text-white/40 text-sm">Reference</span>
                      <span className="font-mono text-white/75 font-semibold text-sm" data-testid="send-done-reference">{statusData.reference}</span>
                      <CopyRefButton reference={statusData.reference} />
                    </div>
                  )}
                </div>
              </div>

              <div className="p-5 sm:p-7 space-y-3.5">
                <ReviewRow label="You sent" value={formatAmount(statusData.send_amount, statusData.send_currency)} />
                {statusData.recipient_receives != null && (
                  <ReviewRow label="Recipient received" value={formatAmount(statusData.recipient_receives, statusData.recipient_currency)} bold />
                )}
                <ReviewRow label="Status" value={<FCSBadge variant={BADGE_VARIANT_FOR_STATUS[statusData.status] || "pending"} label={statusData.label} size="sm" />} />
              </div>

              <div className="px-5 sm:px-7 pb-6 pt-2 flex flex-wrap gap-3">
                <motion.button
                  data-testid="send-done-another"
                  onClick={() => {
                    setStep(0); setQuote(null); setIntent(null); setStatusData(null);
                    setRecipient({ name: "", bank_name: "", bank_code: "", account_number: "", mobile_number: "" });
                    setDestCountry(""); setMethod(""); setAmount(200);
                  }}
                  className="btn-primary" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                >
                  <RefreshCw size={14} /> Send another
                </motion.button>
                {statusData.status !== "payout_completed" && (
                  <button data-testid="send-done-support" onClick={() => navigate("/support")}
                    className="text-sm text-danger hover:opacity-75 transition-opacity flex items-center gap-1.5 px-2">
                    <AlertCircle size={13} /> Contact support
                  </button>
                )}
                <button data-testid="send-done-home" onClick={() => navigate("/app/dashboard")}
                  className="text-sm text-muted-foreground hover:text-navy transition-colors flex items-center gap-1.5 px-2">
                  <Home size={13} /> Return home
                </button>
              </div>
            </div>
          </motion.div>
        )}

      </AnimatePresence>
    </div>
  );
}

/* ── Pay step (Stripe Elements) ─────────────────────────────────── */
function PayStep({ intent, quote, onBack, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  // Same reasoning as FundWalletModal.jsx: keep CardElement mounted
  // through the whole confirm call, and keep the idle-logout clock alive
  // while a card is being typed into a cross-origin Stripe iframe.
  useKeepIdleLogoutAlive(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const confirm = async () => {
    if (!stripe || !elements || !intent.client_secret) return;
    setSubmitting(true);
    setError(null);
    const cardElement = elements.getElement(CardElement);
    const result = await stripe.confirmCardPayment(intent.client_secret, {
      payment_method: { card: cardElement },
    });
    if (result.error) {
      setError(result.error.message || "Payment could not be confirmed.");
      setSubmitting(false);
      return;
    }
    // A successful confirmCardPayment() here only tells this browser the
    // card step succeeded — it is never itself treated as proof the
    // transfer completed. Only the backend's webhook-driven status
    // (polled on the next step) decides that.
    setSubmitting(false);
    onPaid();
  };

  return (
    <div>
      <div className="card-elevated p-6">
        <h3 className="font-display text-xl font-black text-navy mb-0.5 tracking-tight">Pay securely</h3>
        <p className="text-sm text-muted-foreground mb-5">
          {quote && `${formatAmount(Number(quote.send_amount) + Number(quote.fee), quote.source_currency)} — card details never touch MoneyMeet's servers.`}
        </p>
        <div className="input-field py-3">
          <CardElement options={{ disabled: submitting, style: { base: { fontSize: "15px" } } }} />
        </div>
        {error && <p role="alert" className="text-sm text-danger mt-3">{error}</p>}
        <p className="text-[11px] text-muted-foreground mt-3 flex items-center gap-1.5">
          <ShieldCheck size={11} className="text-emerald" /> 256-bit encrypted · Processed by Stripe
        </p>
      </div>
      <div className="mt-5 flex justify-between">
        <button onClick={onBack} disabled={submitting} data-testid="step-back-4"
          className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-navy transition-colors px-2 py-2 disabled:opacity-50">
          <ArrowLeft size={14} /> Back
        </button>
        <motion.button
          data-testid="pay-confirm"
          onClick={confirm}
          disabled={!stripe || submitting}
          className="btn-primary min-w-[160px] justify-center disabled:opacity-60 disabled:cursor-not-allowed"
          whileHover={{ scale: submitting ? 1 : 1.02 }}
          whileTap={{ scale: submitting ? 1 : 0.97 }}
        >
          {submitting ? <><Loader2 size={14} className="animate-spin" /> Confirming…</> : <><Send size={14} /> Pay now</>}
        </motion.button>
      </div>
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────────────── */
function QuotePill({ label, value, highlight }) {
  return (
    <div className={`px-4 py-3.5 ${highlight ? "bg-emerald/[0.06]" : "bg-secondary/70"}`}>
      <p className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-bold mb-0.5">{label}</p>
      <p className={`text-[13px] font-bold num-tabular ${highlight ? "text-emerald" : "text-navy"}`}>{value}</p>
    </div>
  );
}

function ReviewRow({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`${bold ? "font-display font-bold text-base num-tabular" : "font-semibold"} text-navy`}>{value}</span>
    </div>
  );
}

function CopyRefButton({ reference }) {
  const [copied, setCopied] = useState(false);
  return (
    <motion.button
      data-testid="send-done-copy-ref"
      onClick={() => navigator.clipboard.writeText(reference).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })}
      whileTap={{ scale: 0.9 }}
      className="text-white/40 hover:text-white/75 transition-colors"
      aria-label="Copy reference number"
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
    </motion.button>
  );
}
